import { describeError } from '@/utils/errorMessage';
import { notifyError, notifySuccess, notifyWarning } from '@/store/useToastStore';
import { useErrorPopup } from '@/hooks/useErrorPopup';
import { useEffect, useMemo, useState } from 'react';
import { Q } from '@nozbe/watermelondb';
import { Plus, Trash2 } from 'lucide-react';
import { database, txn } from '@/db';
import { Expense, ExpenseCategory } from '@/db/models/System';
import { useAppStore } from '@/store/useAppStore';
import { findOpenSessionId } from '@/billing/useBillingSession';
import { useObservable } from '@/hooks/useObservable';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Dialog, DialogContent } from '@/components/ui/Dialog';
import { formatCurrency } from '@/utils/format';
import { cn } from '@/utils/cn';
import { RBACGate, REPORTS_DAY_CLOSE } from '@/rbac';

const DEFAULT_CATEGORIES = ['Rent', 'Salary', 'Electricity', 'Water', 'Internet', 'Packaging', 'Transport', 'Maintenance', 'Miscellaneous'];
type Range = 'TODAY' | 'MONTH';
type Method = 'CASH' | 'UPI' | 'CARD' | 'BANK';

export function ExpenseListScreen() {
  const activeStoreId = useAppStore((s) => s.activeStoreId);
  const merchantId = useAppStore((s) => s.currentUser?.merchantId);
  const [range, setRange] = useState<Range>('TODAY');
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const categories = useObservable<ExpenseCategory[]>(
    () => merchantId
      ? database.get<ExpenseCategory>('expense_categories').query(
        Q.where('merchant_id', merchantId),
        Q.where('is_deleted', false),
        Q.sortBy('name', Q.asc),
      ).observe()
      : null,
    [merchantId],
    [],
  );

  useEffect(() => {
    if (!merchantId || categories.length > 0) return;
    void txn(async (db) => {
      const collection = db.get<ExpenseCategory>('expense_categories');
      for (const name of DEFAULT_CATEGORIES) {
        await collection.create((record) => {
          record.merchantId = merchantId;
          record.name = name;
          record.version = 1;
          record.isSynced = false;
          record.isDeleted = false;
        });
      }
    });
  }, [merchantId, categories.length]);

  const [from, to] = useMemo(() => dateBounds(range), [range]);
  const expenses = useObservable<Expense[]>(
    () => activeStoreId
      ? database.get<Expense>('expenses').query(
        Q.where('store_id', activeStoreId),
        Q.where('is_deleted', false),
        Q.where('expense_date', Q.gte(from.getTime())),
        Q.where('expense_date', Q.lte(to.getTime())),
        Q.sortBy('expense_date', Q.desc),
      ).observe()
      : null,
    [activeStoreId, from.getTime(), to.getTime()],
    [],
  );

  const categoryName = useMemo(() => {
    const map = new Map<string, string>();
    categories.forEach((c) => map.set(c.id, c.name));
    return map;
  }, [categories]);
  const total = expenses.reduce((sum, expense) => sum + expense.amount, 0);

  if (!activeStoreId || !merchantId) return <div className="text-body text-fg-secondary">No active store. Sign in again.</div>;

  return (
    <div className="flex h-full flex-col gap-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-display text-fg-primary">Expenses</h1>
          <p className="mt-1 text-body text-fg-secondary">
            {expenses.length} entries · {formatCurrency(total)}
          </p>
        </div>
        <RBACGate requires={REPORTS_DAY_CLOSE}>
          <Button onClick={() => setFormOpen(true)}><Plus size={16} /> Add expense</Button>
        </RBACGate>
      </div>

      <div className="flex items-center gap-2">
        <Segment label="Today" active={range === 'TODAY'} onClick={() => setRange('TODAY')} />
        <Segment label="This month" active={range === 'MONTH'} onClick={() => setRange('MONTH')} />
      </div>

      <div className="flex-1 overflow-auto rounded-lg border border-stroke-hairline/10 bg-bg-surface">
        {expenses.length === 0 ? (
          <div className="flex h-full items-center justify-center text-body text-fg-tertiary">
            No expenses recorded for this range.
          </div>
        ) : (
          <table className="w-full text-body">
            <thead className="sticky top-0 bg-bg-surface">
              <tr className="border-b border-stroke-hairline/10 text-label uppercase text-fg-tertiary">
                <th className="px-4 py-2.5 text-left">Expense</th>
                <th className="px-4 py-2.5 text-left">Method</th>
                <th className="px-4 py-2.5 text-left">Date</th>
                <th className="px-4 py-2.5 text-right">Amount</th>
                <th className="px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {expenses.map((expense) => (
                <tr
                  key={expense.id}
                  className="cursor-pointer border-b border-stroke-hairline/5 hover:bg-bg-subtle"
                  onClick={() => setEditingId(expense.id)}
                >
                  <td className="px-4 py-3">
                    <div className="font-medium text-fg-primary">{categoryName.get(expense.categoryId) ?? 'Expense'}</div>
                    <div className="text-label text-fg-tertiary">{expense.description || 'No description'}</div>
                  </td>
                  <td className="px-4 py-3 text-fg-secondary">{expense.paymentMethod ?? 'CASH'}</td>
                  <td className="px-4 py-3 text-fg-secondary">{expense.expenseDate.toLocaleDateString()}</td>
                  <td className="px-4 py-3 text-right font-medium tabular-nums">{formatCurrency(expense.amount)}</td>
                  <td className="px-4 py-3 text-right">
                    <RBACGate requires={REPORTS_DAY_CLOSE}>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); void deleteExpense(expense).then(() => notifySuccess('Expense deleted.'), (err) => notifyError(describeError(err, 'Could not delete the expense.'))); }}
                        className="rounded p-1 text-fg-tertiary hover:bg-danger/10 hover:text-danger-ink"
                        aria-label="Delete expense"
                      >
                        <Trash2 size={14} />
                      </button>
                    </RBACGate>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent title="Add expense">
          <ExpenseForm storeId={activeStoreId} merchantId={merchantId} categories={categories} onDone={() => setFormOpen(false)} />
        </DialogContent>
      </Dialog>

      <Dialog open={!!editingId} onOpenChange={(open) => !open && setEditingId(null)}>
        <DialogContent title="Edit expense">
          {editingId && (
            <ExpenseForm
              storeId={activeStoreId}
              merchantId={merchantId}
              expenseId={editingId}
              categories={categories}
              onDone={() => setEditingId(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function dateBounds(range: Range): [Date, Date] {
  const now = new Date();
  const from = range === 'TODAY'
    ? new Date(now.getFullYear(), now.getMonth(), now.getDate())
    : new Date(now.getFullYear(), now.getMonth(), 1);
  const to = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
  if (range === 'MONTH') to.setMonth(now.getMonth() + 1, 0);
  return [from, to];
}

async function deleteExpense(expense: Expense) {
  await txn(async (db) => {
    await expense.update((record) => {
      record.isDeleted = true;
      record.isSynced = false;
      record.version = expense.version + 1;
    });
  });
}

function ExpenseForm({
  storeId,
  merchantId,
  expenseId,
  categories,
  onDone,
}: {
  storeId: string;
  merchantId: string;
  expenseId?: string;
  categories: ExpenseCategory[];
  onDone: () => void;
}) {
  const [categoryId, setCategoryId] = useState(categories[0]?.id ?? '');
  const [newCategory, setNewCategory] = useState('');
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState<Method>('CASH');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useErrorPopup(error, () => setError(null));

  useEffect(() => {
    if (!expenseId) {
      if (!categoryId && categories[0]) setCategoryId(categories[0].id);
      return;
    }
    let alive = true;
    database.get<Expense>('expenses').find(expenseId).then((expense) => {
      if (!alive) return;
      setCategoryId(expense.categoryId);
      setAmount(String(expense.amount));
      setMethod((expense.paymentMethod as Method) || 'CASH');
      setDate(expense.expenseDate.toISOString().slice(0, 10));
      setDescription(expense.description ?? '');
    }).catch(() => undefined);
    return () => { alive = false; };
  }, [expenseId, categories, categoryId]);

  async function submit() {
    const parsed = Number(amount);
    if (!(parsed > 0)) {
      setError('Enter an amount greater than zero.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      // Resolved outside the writer: it is a read, and a writer that reads is a
      // writer holding the queue open for no reason.
      const sessionId = await findOpenSessionId(storeId, useAppStore.getState().deviceId || '');
      await txn(async (db) => {
        let resolvedCategoryId = categoryId;
        if (newCategory.trim()) {
          const created = await db.get<ExpenseCategory>('expense_categories').create((record) => {
            record.merchantId = merchantId;
            record.name = newCategory.trim();
            record.version = 1;
            record.isSynced = false;
            record.isDeleted = false;
          });
          resolvedCategoryId = created.id;
        }
        if (!resolvedCategoryId) throw new Error('Pick or create a category.');

        if (expenseId) {
          const existing = await db.get<Expense>('expenses').find(expenseId);
          await existing.update((record) => {
            record.categoryId = resolvedCategoryId;
            record.amount = parsed;
            record.paymentMethod = method;
            record.description = description.trim() || null;
            record.expenseDate = new Date(date);
            record.version = existing.version + 1;
            record.isSynced = false;
          });
        } else {
          await db.get<Expense>('expenses').create((record) => {
            record.storeId = storeId;
            record.categoryId = resolvedCategoryId;
            record.amount = parsed;
            record.paymentMethod = method;
            record.description = description.trim() || null;
            record.expenseDate = new Date(date);
            record.receiptUrl = null;
            // The drawer this cash left. Day close used to subtract the whole
            // store's expenses for the calendar day from every open session, so
            // two counters each absorbed the same expense and a session opened
            // in the afternoon absorbed the morning's.
            record.billingSessionId = sessionId;
            record.version = 1;
            record.isSynced = false;
            record.isDeleted = false;
          });
        }
      });
      notifySuccess(expenseId ? 'Expense updated.' : `Expense of ${formatCurrency(parsed)} added.`);
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save expense.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-4 p-5">
      <label className="block">
        <span className="mb-1 block text-label uppercase text-fg-tertiary">Category</span>
        <select
          value={categoryId}
          onChange={(e) => setCategoryId(e.target.value)}
          className="h-9 w-full rounded-md border border-stroke-hairline/20 bg-bg-surface px-3 text-body text-fg-primary"
        >
          <option value="">Pick category</option>
          {categories.map((category) => <option key={category.id} value={category.id}>{category.name}</option>)}
        </select>
      </label>
      <label className="block">
        <span className="mb-1 block text-label uppercase text-fg-tertiary">New category</span>
        <Input value={newCategory} onChange={(e) => setNewCategory(e.target.value)} placeholder="Optional" />
      </label>
      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <span className="mb-1 block text-label uppercase text-fg-tertiary">Amount</span>
          <Input type="number" min="0.01" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
        </label>
        <label className="block">
          <span className="mb-1 block text-label uppercase text-fg-tertiary">Date</span>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </label>
      </div>
      <div>
        <span className="mb-1 block text-label uppercase text-fg-tertiary">Payment method</span>
        <div className="flex flex-wrap gap-2">
          {(['CASH', 'UPI', 'CARD', 'BANK'] as Method[]).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMethod(m)}
              className={cn('rounded-md px-3 py-1.5 text-body', method === m ? 'btn-brand border border-white/10' : 'btn-brand-soft border border-ink-500/25')}
            >
              {m}
            </button>
          ))}
        </div>
      </div>
      <label className="block">
        <span className="mb-1 block text-label uppercase text-fg-tertiary">Description</span>
        <Input value={description} onChange={(e) => setDescription(e.target.value)} />
      </label>
      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onDone} disabled={saving}>Cancel</Button>
        <Button onClick={submit} disabled={saving}>{saving ? 'Saving...' : 'Save expense'}</Button>
      </div>
    </div>
  );
}

function Segment({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-md px-3 py-1.5 text-body transition-colors',
        active ? 'bg-accent/10 text-accent-ink' : 'text-fg-secondary hover:bg-bg-subtle hover:text-fg-primary',
      )}
    >
      {label}
    </button>
  );
}
